import { parseMarketplaceId, type MarketplaceId } from "./marketplace-id"
import type { CopyEvaluationIssue, ProductFacts } from "./schemas"

// Preserve the closed marketplace lookup and existing alphabets. A reviewed
// identity exempts its exact token, never a letter or arbitrary source wording.
const COPY_LOCALES = {
  sk: "sk",
  fr: "fr",
  it: "it",
  de: "de",
  cs: "cs",
  pl: "pl",
} as const
type CopyLocale = (typeof COPY_LOCALES)[keyof typeof COPY_LOCALES]

const MARKETPLACE_LOCALE: Record<string, CopyLocale> = {
  "drmax-sk": COPY_LOCALES.sk,
  "benu-it": COPY_LOCALES.it,
  "empik-pl": COPY_LOCALES.pl,
  "cdiscount-fr": COPY_LOCALES.fr,
  "carrefour-fr": COPY_LOCALES.fr,
  "greenweez-fr": COPY_LOCALES.fr,
  "decathlon-it": COPY_LOCALES.it,
  "shop-apotheke-it": COPY_LOCALES.it,
  "eprice-it": COPY_LOCALES.it,
  "shop-apotheke-de": COPY_LOCALES.de,
  // AT lists in German too: the title gate is keyed on LANGUAGE, not country.
  "shop-apotheke-at": COPY_LOCALES.de,
  "decathlon-cz": COPY_LOCALES.cs,
  "decathlon-pl": COPY_LOCALES.pl,
}

// The accented/diacritic letters each locale legitimately uses on top of plain
// ASCII a–z. Lowercase only — the check folds case before testing. A title
// letter that is neither ASCII nor in this set is foreign-script for the locale.
const LOCALE_EXTRA_LETTERS: Record<CopyLocale, ReadonlySet<string>> = {
  sk: new Set("áäčďéíĺľňóôŕšťúýž".split("")),
  // French: acute/grave/circumflex e, à â, ù û, ç, î ï, ô, ë ÿ, the æ/œ
  // ligatures. Notably NOT á í ý ž š č etc. — those are CZ/SK/PL.
  fr: new Set("éèêëàâùûüçîïôöÿœæ".split("")),
  // Italian: only grave/acute vowels.
  it: new Set("àèéìíîòóù".split("")),
  // German: umlauts + eszett.
  de: new Set("äöüß".split("")),
  // Czech: full set of carons, acutes, the ring, ě, ů.
  cs: new Set("áčďéěíňóřšťúůýž".split("")),
  // Polish: ogoneks, kreska, the slashed ł, dotted ż.
  pl: new Set("ąćęłńóśźż".split("")),
}

function localeForMarketplace(marketplace: MarketplaceId): CopyLocale | undefined {
  // Validate the id through the shared parser, then look it up whole.
  const id = parseMarketplaceId(marketplace)
  return MARKETPLACE_LOCALE[id]
}

// Reviewed identities from #968 and the immutable Dr Max title-locale capture.
// Recognition is deliberately closed; capitalization/frequency is not evidence.
const REVIEWED_LINE_NAMES = [
  "MoiMüv Protein Spread",
  "MoiMüv Protein Wafer",
  "MoiMüv Protein Crispies",
] as const

type TitleLocaleInput = {
  title: string
  marketplace: MarketplaceId
  facts?: ProductFacts | undefined
  simpleSku?: string | null | undefined
}

function reviewedLineName(input: TitleLocaleInput): string | undefined {
  const facts = input.facts
  if (
    input.marketplace !== "drmax-sk" ||
    facts?.marketplace !== input.marketplace ||
    facts.source.storeCode !== "gymbeamsk" ||
    facts.source.locale !== "sk" ||
    facts.config.brand !== "GymBeam Food"
  ) {
    return undefined
  }
  const line = REVIEWED_LINE_NAMES.find(
    (name) => facts.config.name.localized === `${name} - GymBeam`
  )
  if (!line || input.simpleSku == null) {
    return line
  }
  const simple = facts.simples.find((row) => row.sku === input.simpleSku)
  // A selected target must prove its own whole name. Family/sibling names or
  // arbitrary suffix prose cannot supply that authority.
  return simple?.size.label &&
    simple.flavor?.label &&
    simple.name === `${line} - GymBeam ${simple.size.label} - ${simple.flavor.label}`
    ? line
    : undefined
}

/** Shared locale guard for evaluation and Dr Max's normal validated writers. */
export function copyTitleLocaleIssues(input: TitleLocaleInput): CopyEvaluationIssue[] {
  const locale = localeForMarketplace(input.marketplace)
  if (!locale) {
    return []
  }
  const line = reviewedLineName(input)
  const protectedStarts = new Set<number>()
  if (line) {
    // No regex syntax occurs in the closed reviewed names. Unicode word interiors,
    // combining marks, joiners and attached prefixes/suffixes cannot split identity.
    // JS whitespace includes the format character U+FEFF. Exclude all Cf at
    // both boundaries, including whitespace after accepted terminal punctuation.
    const whitespace = "[^\\S\\p{Cf}]"
    const identity = new RegExp(
      `(?<!\\S)(?<!\\p{Cf})${line}(?=$|${whitespace}|[),.;:!?]+(?=$|${whitespace}))`,
      "gu"
    )
    for (const match of input.title.matchAll(identity)) {
      protectedStarts.add(match.index)
    }
  }
  const issues: CopyEvaluationIssue[] = []
  if (line && protectedStarts.size === 0) {
    issues.push({
      severity: "error",
      code: "title_missing_line",
      message: `Keep the exact source-backed product line "${line}". Do not drop, translate or transliterate its identity.`,
    })
  }
  const foreignWords = new Set<string>()
  for (const match of input.title.matchAll(/[\p{L}]+/gu)) {
    const word = match[0]
    if (word === "MoiMüv" && protectedStarts.has(match.index)) {
      continue
    }
    if (wordHasForeignLetter(word, LOCALE_EXTRA_LETTERS[locale])) {
      foreignWords.add(word)
    }
  }
  if (foreignWords.size > 0) {
    issues.push({
      severity: "error",
      code: "title_foreign_locale",
      message: `title contains word(s) not in the ${locale} alphabet — "${[...foreignWords].join('", "')}". Write surrounding text in ${locale}; only exact reviewed source-backed identities are exempt. Prepare again when source evidence is missing.`,
    })
  }
  return issues
}

/** A word is foreign-script for the locale when any letter is neither plain
 *  ASCII a–z nor one of the locale's legitimate accented letters. Case is folded
 *  to lowercase first so the set need only list lowercase forms. */
function wordHasForeignLetter(word: string, allowedExtra: ReadonlySet<string>): boolean {
  for (const letter of word.toLowerCase()) {
    if (/[a-z]/.test(letter)) {
      continue
    }
    if (allowedExtra.has(letter)) {
      continue
    }
    return true
  }
  return false
}
